import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { checkFullTokenValidation, getUserRole, getAccessToken } from 'utils/auth/auth';
import { AppDispatch } from 'config/redux/store';
import Spinner from 'components/common/Spinner';

// Define interface for component props
interface ProtectedRouteProps {
  allowedRoles?: string[];
}

// Define component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles = [] }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const token = getAccessToken();

  useEffect(() => {
    const validate = async () => {
      // Check if just logged in to skip redundant validation
      if (localStorage.getItem('justLoggedIn') === 'true') {
        setIsAuthorized(true);
        return;
      }

      if (!token) {
        setIsAuthorized(false);
        return;
      }

      const isValid = await checkFullTokenValidation(token, dispatch);

      if (!isValid) {
        setIsAuthorized(false);
        return;
      }

      const userRole = getUserRole();

      // Case-insensitive role check (also handles spaces in role names like "Super Admin")
      if (allowedRoles.length > 0) {
        if (!userRole) {
          setIsAuthorized(false);
          return;
        }
        // Normalize by removing spaces and converting to lowercase
        const normalizedUserRole = userRole.toLowerCase().replace(/\s+/g, '');
        const hasRole = allowedRoles.some(role => role.toLowerCase().replace(/\s+/g, '') === normalizedUserRole);
        if (!hasRole) {
          setIsAuthorized(false);
          return;
        }
      }

      setIsAuthorized(true);
    };

    validate();
  }, [token, dispatch, allowedRoles]);

  if (isAuthorized === null) {
    return <Spinner size="large" text="Loading..." fullPage />;
  }

  return isAuthorized ? <Outlet /> : <Navigate to="/login" replace />;
};

export default ProtectedRoute;